import { useState } from "react";
import { Bot, Check, Copy, LoaderCircle, Send, ShieldCheck } from "lucide-react";
import { ApiError, requestAi } from "../lib/api";
import type {
  AiAction,
  AiAssistResult,
  ProposedTask,
  WeeklyDraftResult
} from "../types";
import { Button, Field, Modal } from "./ui";

export function AiProposalDialog({
  action,
  selectedText,
  context,
  sourceLabel,
  onClose,
  onApplyTasks,
  onApplyNextStep,
  onApplyWeekly
}: {
  action: AiAction;
  selectedText: string;
  context?: Record<string, unknown>;
  sourceLabel: string;
  onClose: () => void;
  onApplyTasks?: (tasks: ProposedTask[]) => Promise<void>;
  onApplyNextStep?: (nextStep: string) => Promise<void>;
  onApplyWeekly?: (draft: WeeklyDraftResult) => void;
}) {
  const [started, setStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<AiAssistResult | null>(null);
  const [selectedTasks, setSelectedTasks] = useState<number[]>([]);
  const [applied, setApplied] = useState(false);

  async function ask() {
    setStarted(true);
    setLoading(true);
    setError("");
    try {
      const proposal = await requestAi({ action, selectedText, context });
      setResult(proposal);
      if (proposal.action === "extract_tasks") {
        setSelectedTasks(proposal.tasks.map((_, index) => index));
      }
    } catch (requestError) {
      setError(
        requestError instanceof ApiError
          ? requestError.message
          : "AI 暂时没有返回可用结果，请稍后再试。"
      );
    } finally {
      setLoading(false);
    }
  }

  async function applyTasks() {
    if (!result || result.action !== "extract_tasks" || !onApplyTasks) return;
    const tasks = selectedTasks.map((index) => result.tasks[index]).filter(Boolean);
    await onApplyTasks(tasks);
    setApplied(true);
  }

  async function applyNextStep() {
    if (!result || result.action !== "clarify_thought" || !onApplyNextStep) return;
    await onApplyNextStep(result.suggestedNextStep);
    setApplied(true);
  }

  function applyWeekly() {
    if (!result || result.action !== "draft_weekly_review" || !onApplyWeekly) return;
    onApplyWeekly(result);
    setApplied(true);
  }

  const actionTitle = {
    clarify_thought: "把想法再想清楚",
    extract_tasks: "整理成可执行待办",
    draft_weekly_review: "生成周复盘草案"
  }[action];

  return (
    <Modal
      title={actionTitle}
      eyebrow="DeepSeek · 按需辅助"
      onClose={onClose}
      size="large"
      footer={
        applied ? (
          <Button type="button" variant="primary" onClick={onClose}>
            完成
          </Button>
        ) : started && result ? (
          <>
            <span className="ai-footer-note">只有点击采用后才会写入</span>
            <Button type="button" onClick={onClose}>
              取消
            </Button>
            {result.action === "extract_tasks" && (
              <Button
                type="button"
                variant="primary"
                disabled={selectedTasks.length === 0}
                onClick={() => void applyTasks()}
              >
                <Check size={16} /> 写入 {selectedTasks.length} 项待办
              </Button>
            )}
            {result.action === "clarify_thought" && (
              <Button type="button" variant="primary" onClick={() => void applyNextStep()}>
                采用建议下一步
              </Button>
            )}
            {result.action === "draft_weekly_review" && (
              <Button type="button" variant="primary" onClick={applyWeekly}>
                填入复盘表单
              </Button>
            )}
          </>
        ) : (
          <>
            <Button type="button" onClick={onClose}>
              取消
            </Button>
            {!started && (
              <Button type="button" variant="primary" onClick={() => void ask()}>
                <Send size={16} /> 确认发送给 DeepSeek
              </Button>
            )}
            {started && !loading && error && (
              <Button type="button" variant="primary" onClick={() => void ask()}>
                重试
              </Button>
            )}
          </>
        )
      }
    >
      {!started && (
        <div className="ai-consent">
          <div className="ai-consent-icon">
            <ShieldCheck size={26} />
          </div>
          <div>
            <h3>只发送本次选中的内容</h3>
            <p>
              将把「{sourceLabel}」的文字发送给 DeepSeek 处理。DeepSeek
              会返回建议，但不会直接修改工作台；你确认采用后才会写入。
            </p>
            <div className="selection-preview">{selectedText.slice(0, 420)}{selectedText.length > 420 ? "…" : ""}</div>
          </div>
        </div>
      )}

      {started && loading && (
        <div className="ai-loading">
          <LoaderCircle size={30} className="spin" />
          <h3>正在整理</h3>
          <p>只基于你选中的材料，不读取其他记录。</p>
        </div>
      )}

      {started && !loading && error && (
        <div className="ai-error">
          <Bot size={28} />
          <h3>这次没有形成结果</h3>
          <p>{error}</p>
          <p className="muted">原始记录没有发生任何变化，可以稍后重试或继续手写。</p>
        </div>
      )}

      {started && !loading && result?.action === "clarify_thought" && (
        <div className="ai-result">
          <div className="ai-section">
            <p className="eyebrow">当前理解</p>
            <p>{result.summary}</p>
          </div>
          <div className="ai-question-list">
            {result.questions.map((question, index) => (
              <article key={`${question}-${index}`}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <p>{question}</p>
              </article>
            ))}
          </div>
          <Field label="建议下一步">
            <textarea className="textarea" rows={3} value={result.suggestedNextStep} readOnly />
          </Field>
          <Button
            type="button"
            variant="ghost"
            onClick={() => void navigator.clipboard.writeText(result.questions.join("\n"))}
          >
            <Copy size={16} /> 复制追问
          </Button>
        </div>
      )}

      {started && !loading && result?.action === "extract_tasks" && (
        <div className="ai-result">
          <p className="muted">检查一下，只保留你真的准备推进的事项。</p>
          <div className="proposal-list">
            {result.tasks.map((task, index) => {
              const checked = selectedTasks.includes(index);
              return (
                <label key={`${task.title}-${index}`} className={checked ? "proposal-card selected" : "proposal-card"}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      setSelectedTasks((current) =>
                        checked ? current.filter((item) => item !== index) : [...current, index]
                      )
                    }
                  />
                  <div>
                    <h3>{task.title}</h3>
                    <p><strong>预期结果：</strong>{task.outcome || "未明确"}</p>
                    <p><strong>下一步：</strong>{task.nextAction || "未明确"}</p>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {started && !loading && result?.action === "draft_weekly_review" && (
        <div className="ai-result draft-result">
          <div>
            <p className="eyebrow">真正推进</p>
            <p>{result.results}</p>
          </div>
          <div>
            <p className="eyebrow">阻碍与未完成</p>
            <p>{result.blockers}</p>
          </div>
          <div>
            <p className="eyebrow">需要修正的判断</p>
            <p>{result.judgmentCorrection}</p>
          </div>
          <div>
            <p className="eyebrow">下周建议聚焦</p>
            {result.nextWeekFocus.map((item) => (
              <p key={item}>· {item}</p>
            ))}
          </div>
        </div>
      )}

      {applied && (
        <div className="applied-banner">
          <Check size={18} /> 已按你的选择写入
        </div>
      )}
    </Modal>
  );
}
